/**
 * Data-layer entry point.
 *
 * Services import from here:
 *
 *   import { http, USE_MOCKS, apiResolve, apiEcho } from '@/api'
 *
 * The two shapes services follow today:
 *
 *   if (USE_MOCKS) return apiResolve(fixture, { signal, path })
 *   return http.get('/things', { signal })
 *
 * Once the backend is up, set `VITE_USE_MOCKS=false` and the same
 * service modules switch to the live `http` calls. No store or
 * page changes needed.
 *
 * What lives in this folder:
 *
 *   client.js          HTTP client + default interceptors (auth, refresh)
 *   config.js          env-driven config (base URL, timeouts, mock
 *                      timing, mock error rate, flags, …)
 *   errors.js          typed errors: HttpError / AuthError / ValidationError
 *                      / NetworkError / TimeoutError + withRetry + isTransient
 *   interceptors.js    request/response/error registry (axios-style)
 *   tokens.js          access/refresh token storage abstraction
 *   pagination.js      page/cursor helpers + normalized list response
 *   realtime.js        WebSocket / SSE adapter (placeholder)
 *   mock-simulator.js  production-like mock layer (latency presets,
 *                      random error injection, abort signal support,
 *                      request deduplication)
 */

import config from './config'
import { http } from './client'
import { mockRequest, mockEcho, dedupe, isAbortError } from './mock-simulator'
import type { MockRequestOptions } from './mock-simulator'

export { config }
export { http }
export * from './errors'
export * from './pagination'
export { interceptors } from './interceptors'
export { getAccessToken, getRefreshToken, setTokens, clearTokens, shouldRefresh } from './tokens'
export { realtime } from './realtime'

/* Mock-simulator surface — services + stores import these. */
export { mockRequest, mockEcho, dedupe, isAbortError }

/* Cache freshness primitive — stores opt in by tracking
 * lastFetched + cacheTTLms in state. */
export { CACHE_TTL, isStale, touchCache, invalidate, onInvalidate } from './cache'
export {
  setMockTiming,
  setMockErrorRate,
  forceNextError,
  resetMockSimulator,
} from './mock-simulator'

/** True while the app is on mock fixtures. */
export const USE_MOCKS = config.useMocks

/**
 * Resolve a mock value asynchronously, simulating production
 * network behaviour:
 *   - random latency from the configured timing preset
 *   - optional random error injection
 *   - AbortSignal-aware (cancels the awaited delay)
 *
 *   await apiResolve(fixture)                       // back-compat
 *   await apiResolve(fixture, 80)                   // legacy: delay in ms
 *   await apiResolve(fixture, { signal, path })     // new: rich opts
 *
 * Always returns a deep clone so callers may mutate the result
 * without polluting the underlying fixture.
 */
export async function apiResolve<T>(value: T, opts?: MockRequestOptions | number): Promise<T> {
  /* Legacy signature support: apiResolve(value, 120) */
  const options = typeof opts === 'number' ? { delayMs: opts } : opts
  return mockRequest(value, options)
}

/** Mock write: echo payload + synthetic id back. */
export async function apiEcho<T extends object>(
  payload: T,
  opts?: (MockRequestOptions & { id?: string | number }) | number
): Promise<T & { id: string | number }> {
  const options = typeof opts === 'number' ? { delayMs: opts } : opts
  return mockEcho(payload, options)
}
